// Utility for managing pending event registrations during authentication flow

export interface PendingRegistration {
  eventId: string;
  eventTitle: string;
  role: 'sponsor' | 'participant';
  timestamp: number;
}

const STORAGE_KEY = 'pending_registration';
const TTL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Set pending registration to localStorage
 */
export const setPendingRegistration = (
  eventId: string,
  eventTitle: string,
  role: 'sponsor' | 'participant'
): void => {
  const pending: PendingRegistration = {
    eventId,
    eventTitle,
    role,
    timestamp: Date.now(),
  };

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch (error) {
    console.error('Failed to save pending registration:', error);
  }
};

/**
 * Get pending registration from localStorage
 * Returns null if expired or doesn't exist
 */
export const getPendingRegistration = (): PendingRegistration | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return null;

    const pending: PendingRegistration = JSON.parse(stored);

    // Check if expired
    const age = Date.now() - pending.timestamp;
    if (age > TTL_MS) {
      clearPendingRegistration();
      return null;
    }

    return pending;
  } catch (error) {
    console.error('Failed to retrieve pending registration:', error);
    return null;
  }
};

/**
 * Clear pending registration from localStorage
 */
export const clearPendingRegistration = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear pending registration:', error);
  }
};

/**
 * Check if there's a valid pending registration
 */
export const hasPendingRegistration = (): boolean => {
  return getPendingRegistration() !== null;
};
